// „Übergaben“ (Screen hausmeister/04): die vom Büro zugewiesenen Übergaben aus
// ladeMeineUebergaben – Öffnen startet wie bisher den Assistenten (bereiteUebergabeVor).
// Termin = „Datum / Uhrzeit“ aus Schritt 1; statt Fortschrittsbalken das tatsächlich Erfasste.
import { CalendarBlank, CircleNotch, Key, ListChecks, MapPin, Users } from '@phosphor-icons/react';
import { STATUS_TEXT, terminText, type UebergabeStatus } from '../../lib/hausmeister/uebergabeAnzeige';
import { IconChip, SeitenTitel, StatusChip, farbKlasse } from '../../components/portal/bausteine';
import { ART_TEXT, type HausmeisterUebergabe } from '../../lib/hausmeister/uebergaben';
import { ART_STIL } from './symbole';

const STATUS_TON: Record<UebergabeStatus, 'neutral' | 'warn' | 'info'> = { geplant: 'neutral', in_erfassung: 'warn', abschluss_offen: 'info' };
const STATUS_TIP: Record<UebergabeStatus, string> = {
  geplant: 'Übergabe starten', in_erfassung: 'Übergabe fortsetzen', abschluss_offen: 'Abschluss erneut anstoßen',
};

export function uebergabeStatus(u: HausmeisterUebergabe): { text: string; ton: 'neutral' | 'warn' | 'info'; tip: string } {
  return { text: STATUS_TEXT[u.anzeigeStatus], ton: STATUS_TON[u.anzeigeStatus], tip: STATUS_TIP[u.anzeigeStatus] };
}

export function uebergabeTitel(u: HausmeisterUebergabe): string {
  return u.einheit ? `${ART_TEXT[u.art]} · ${u.einheit}` : ART_TEXT[u.art];
}

export default function UebergabenListe({ uebergaben, bereitetVor, onOeffnen }: {
  uebergaben: HausmeisterUebergabe[];
  bereitetVor: string | null;
  onOeffnen: (u: HausmeisterUebergabe) => void;
}) {
  return (
    <div className="p-rein" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <SeitenTitel oben="Mir zugewiesen" titel="Übergaben" />
      {uebergaben.length === 0 ? (
        <div className="p-glas" style={{ borderRadius: 28, padding: '48px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          <Key size={34} style={{ color: 'var(--p-w45)' }} />
          <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--p-w55)' }}>Keine Übergaben zugewiesen</span>
        </div>
      ) : (
        <div className="hm-karten">
          {uebergaben.map((u) => {
            const stil = ART_STIL[u.art];
            const st = uebergabeStatus(u);
            const laedt = bereitetVor === u.protokollId;
            return (
              <button
                key={u.protokollId}
                type="button"
                disabled={!!bereitetVor}
                onClick={() => onOeffnen(u)}
                data-tip={st.tip}
                className={`p-glas p-hebt ${farbKlasse(stil.farbe)}`}
                style={{ width: '100%', textAlign: 'left', color: 'var(--p-w)', cursor: bereitetVor ? 'progress' : 'pointer', border: 'none', padding: 16, display: 'flex', flexDirection: 'column', gap: 12, opacity: bereitetVor && !laedt ? 0.6 : 1 }}
              >
                <div className="p-glas-schimmer-links" style={{ background: 'radial-gradient(90% 120% at 0% 0%, rgba(var(--rgb),0.26), rgba(var(--rgb),0) 60%)' }}>
                  <stil.icon size={130} weight="duotone" className="p-wasserzeichen" style={{ right: 20, bottom: -34, opacity: 0.12 }} />
                </div>
                <div className="p-glas-rand" />
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <IconChip farbe={stil.farbe} icon={stil.icon} groesse={50} radius={15} gedreht />
                  <span style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ fontSize: 16, fontWeight: 800, overflowWrap: 'anywhere' }}>{uebergabeTitel(u)}</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--p-w55)', display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
                      <MapPin size={12} weight="fill" style={{ flexShrink: 0 }} /><span className="p-kuerzen">{u.adresse || u.objekt}</span>
                    </span>
                  </span>
                  {laedt ? <CircleNotch size={18} weight="bold" className="animate-spin" /> : <StatusChip ton={st.ton} gross>{st.text}</StatusChip>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px 14px', flexWrap: 'wrap', fontSize: 12.5, fontWeight: 700, color: 'var(--p-w60)' }}>
                  {terminText(u.datum) && <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><CalendarBlank size={14} />{terminText(u.datum)}</span>}
                  {(u.parteien || u.mieter) && <span style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0, overflowWrap: 'anywhere' }}><Users size={14} />{u.parteien || u.mieter}</span>}
                  {u.vorlageId && <StatusChip ton="info">mit Vorlage</StatusChip>}
                </div>
                {u.anzeigeStatus !== 'geplant' && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: 'var(--p-w50)' }}>
                    <ListChecks size={14} />{u.erfasst}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
